import { z } from 'zod';

import { api } from '@/shared/api';
import { unwrap, unwrapCollection, type Page } from '@/shared/api/envelope';

import {
    toCreditPackage,
    toLedgerEntry,
    toSubscriptionOverview,
    toTopUpPayment,
    toWallet,
    type CreditPackage,
    type InvoiceLink,
    type LedgerEntry,
    type LedgerFilters,
    type Payment,
    type SubscriptionOverview,
    type TopUpPayment,
    type Wallet,
} from './model';
import {
    creditPackageResourceSchema,
    creditTransactionResourceSchema,
    creditWalletResourceSchema,
    invoiceLinkResourceSchema,
    paymentResourceSchema,
    subscriptionOverviewResourceSchema,
} from './types';

/** `meta.payments_test_mode` naast een abonnementsoverzicht; ontbreekt hij, dan is het echt. */
const subscriptionMetaSchema = z.object({
    meta: z.object({ payments_test_mode: z.boolean().optional() }).optional(),
});

function overviewFrom(payload: unknown): SubscriptionOverview {
    const meta = subscriptionMetaSchema.safeParse(payload);

    return toSubscriptionOverview(
        unwrap(subscriptionOverviewResourceSchema, payload),
        meta.success ? meta.data.meta?.payments_test_mode === true : false,
    );
}

/**
 * Het tegoed van de actieve omgeving (`02-api-contract.md` §Facturatie en
 * tegoed). Zien en opwaarderen mag alleen `owner`.
 * Platformbeheer boekt handmatig via `platformApi.bookCredits` in
 * `entities/tenant`.
 */
export const creditApi = {
    async wallet(): Promise<Wallet> {
        return toWallet(unwrap(creditWalletResourceSchema, await api.get('/credits/wallet')));
    },

    /**
     * Het grootboek, nieuwste boeking eerst. Alleen inzien: de API kent geen
     * wijziging of verwijdering van een boeking.
     */
    async transactions(filters: LedgerFilters = {}): Promise<Page<LedgerEntry>> {
        const page = unwrapCollection(
            creditTransactionResourceSchema,
            await api.get('/credits/transactions', {
                query: {
                    'filter[type]': filters.type || undefined,
                    'filter[reference_type]': filters.referenceType || undefined,
                    'filter[reference_uuid]': filters.referenceUuid || undefined,
                    'filter[from]': filters.from || undefined,
                    /* `filter[to]` vergelijkt met een tijdstip; een datum alleen is middernacht. */
                    'filter[to]': filters.to ? `${filters.to} 23:59:59` : undefined,
                    page: filters.page,
                    per_page: filters.perPage,
                },
            }),
        );

        return { items: page.items.map(toLedgerEntry), meta: page.meta };
    },

    async packages(): Promise<CreditPackage[]> {
        return unwrapCollection(
            creditPackageResourceSchema,
            await api.get('/credits/packages'),
        ).items.map(toCreditPackage);
    },

    /**
     * `GET /credits/payments/{payment}` - hoe één opwaardering ervoor staat. De
     * terugkeer van de betaalpagina zegt daar niets over: ook wie afbreekt of wiens
     * betaling mislukt, komt terug. Een betaling van een ander bedrijf is een 404.
     */
    async payment(paymentUuid: string): Promise<TopUpPayment> {
        return toTopUpPayment(
            unwrap(paymentResourceSchema, await api.get(`/credits/payments/${paymentUuid}`)),
        );
    },

    /**
     * `POST /credits/topup` - een pakket kiezen geeft een betaling met een
     * betaallink (201). Er wordt nog niets geboekt; `redirectUrl` brengt de
     * gebruiker na het betalen terug.
     */
    async topUp(input: {
        packageUuid: string;
        redirectUrl: string;
        idempotencyKey: string;
    }): Promise<TopUpPayment> {
        return toTopUpPayment(
            unwrap(
                paymentResourceSchema,
                await api.post(
                    '/credits/topup',
                    { package: input.packageUuid, redirect_url: input.redirectUrl },
                    { idempotencyKey: input.idempotencyKey, cacheKey: false },
                ),
            ),
        );
    },

    /* ---------------------------------------------------------------------- */
    /* Het abonnement bij Smartkeur (`02-api-contract.md` §Abonnement)          */
    /* ---------------------------------------------------------------------- */

    /** `GET /billing/subscription` - hoe het abonnement ervoor staat, en wat een keuring nu kost. */
    async subscription(): Promise<SubscriptionOverview> {
        return overviewFrom(await api.get('/billing/subscription'));
    },

    /**
     * `POST /billing/subscription` - een abonnement kiezen. Het antwoord is de
     * eerste betaling met haar betaalpagina; er loopt nog niets tot die betaald is.
     * Geen idempotency key: een betaling openen wordt nooit vanzelf herhaald.
     */
    async chooseSubscription(planCode: string): Promise<Payment> {
        return toTopUpPayment(
            unwrap(
                paymentResourceSchema,
                await api.post('/billing/subscription', { plan: planCode }, { cacheKey: false }),
            ),
        );
    },

    /** `PATCH /billing/subscription` - wisselen per de volgende abonnementsmaand. */
    async changeSubscription(planCode: string): Promise<SubscriptionOverview> {
        return overviewFrom(
            await api.patch('/billing/subscription', { plan: planCode }, { cacheKey: false }),
        );
    },

    /** `DELETE /billing/subscription` - opzeggen; het abonnement loopt tot het einde van de betaalde maand. */
    async cancelSubscription(): Promise<SubscriptionOverview> {
        return overviewFrom(await api.delete('/billing/subscription', { cacheKey: false }));
    },

    /** `POST /billing/subscription/resume` - een opzegging terugdraaien vóór het einde van de maand. */
    async resumeSubscription(): Promise<SubscriptionOverview> {
        return overviewFrom(
            await api.post('/billing/subscription/resume', {}, { cacheKey: false }),
        );
    },

    /** `POST /billing/subscription/payment` - opnieuw betalen na een mislukte incasso. */
    async payArrears(): Promise<Payment> {
        return toTopUpPayment(
            unwrap(
                paymentResourceSchema,
                await api.post('/billing/subscription/payment', {}, { cacheKey: false }),
            ),
        );
    },

    /** `POST /billing/purchases` - keuringen kopen; welke en tegen welke prijs bepaalt de server. */
    async buyInspections(quantity: number): Promise<Payment> {
        return toTopUpPayment(
            unwrap(
                paymentResourceSchema,
                await api.post('/billing/purchases', { quantity }, { cacheKey: false }),
            ),
        );
    },

    /** `GET /billing/payments` - wat het bedrijf aan Smartkeur betaalde, nieuwste eerst. */
    async payments(page = 1): Promise<Page<Payment>> {
        const result = unwrapCollection(
            paymentResourceSchema,
            await api.get('/billing/payments', { query: { page } }),
        );

        return { items: result.items.map(toTopUpPayment), meta: result.meta };
    },

    /** `GET /billing/payments/{uuid}/invoice` - een tijdelijke link naar de PDF van de factuur. */
    async invoiceLink(paymentUuid: string): Promise<InvoiceLink> {
        const link = unwrap(
            invoiceLinkResourceSchema,
            await api.get(`/billing/payments/${paymentUuid}/invoice`, { cacheKey: false }),
        );

        return { url: link.url, filename: link.filename };
    },
};
